"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { ItemImage } from "@/lib/types";
import imageUtils from "@/lib/imageUtils";
import type { ImageMetadata } from "@/lib/imageUtils";
import { fetchListingPhotoCoachSettings } from "@/lib/listingPhotoCoachSettings";
import {
  LISTING_IMAGE_COMPRESS_TIMEOUT_MS,
  validateListingImageFile,
} from "@/lib/listingImageValidation";
import {
  buildListingImageSignature,
  computeListingImageContentHashFromBlob,
} from "@/lib/listing/listingPhotoDuplicateCheck";
import {
  collectListingDraftContentHashes,
  listingDraftHasMatchingContentHash,
} from "@/lib/listing/listingImageMasterFormat";
import { shouldHardDeleteListingImage } from "@/lib/listing/editListingImagesState";
import { captureListingPhotoGateResult } from "@/lib/analytics/captureListingPhotoFriction";
import { withTimeout } from "@/lib/withTimeout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { v4 as uuidv4 } from "uuid";
import OptimizedImage from "@/components/common/OptimizedImage";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { withMemo } from "@/components/common/withMemo";

interface ImageUploaderProps {
  itemId: string;
  onImageUploaded?: (image: ItemImage) => void;
  onImageDeleted?: (
    imageId: string,
    action?: "mark" | "unmark" | "delete",
  ) => void;
  onMainImageChanged?: (imageId: string) => void;
  initialImages?: ItemImage[];
  maxImages?: number;
  forcePermission?: boolean;
  previewMode?: boolean;
}

type UploadStage = "checking" | "compressing" | "finishing" | null;

function getUploadStatusText(
  stage: UploadStage,
  isDragActive: boolean,
  availableSlots: number,
): string {
  if (isDragActive) {
    return "Drop the images here...";
  }
  if (stage === "checking") {
    return "Checking photo quality…";
  }
  if (stage === "compressing") {
    return "Compressing photo…";
  }
  if (stage === "finishing") {
    return "Saving photo…";
  }
  return `Click or drag & drop up to ${availableSlots} image${availableSlots > 1 ? "s" : ""}`;
}

function ImageUploader({
  itemId,
  onImageUploaded,
  onImageDeleted,
  onMainImageChanged,
  initialImages = [],
  maxImages = 3,
  forcePermission = false,
  previewMode = false,
}: ImageUploaderProps) {
  const [images, setImages] = useState<ItemImage[]>(initialImages);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadStage, setUploadStage] = useState<UploadStage>(null);
  const [error, setError] = useState<string>("");
  const { user } = useAuth();
  const userId = user?.id;
  const { showToast } = useToast();
  const [uploadedContentHashes, setUploadedContentHashes] = useState(
    new Set<string>(),
  );

  // Keep track of all images, including ones being processed
  const [processingCount, setProcessingCount] = useState(0);

  // Ref to track current images for cleanup
  const imagesRef = useRef<ItemImage[]>(images);

  // Update ref whenever images change
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Sync with initialImages when they change
  useEffect(() => {
    const timer = setTimeout(() => {
      setImages(initialImages);
      // Reset content-hash tracking when initialImages change to prevent stale tracking
      setUploadedContentHashes(new Set());
    }, 0);
    return () => clearTimeout(timer);
  }, [initialImages]);

  // Cleanup blob URLs only when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup any blob URLs when component unmounts
      imagesRef.current.forEach((image) => {
        if (image.url && image.url.startsWith("blob:")) {
          URL.revokeObjectURL(image.url);
        }
      });
    };
  }, []); // Empty dependency array - only run on unmount

  // Check if an image is a duplicate based on its file properties
  const isMarkedForDelete = useCallback((image: ItemImage): boolean => {
    const marker = image.metadata?.__pendingDelete;
    return marker === true;
  }, []);

  const activeImagesCount = images.filter(
    (image) => !isMarkedForDelete(image),
  ).length;

  const hasContentHashConflict = useCallback(
    (hash?: string) => {
      if (!hash) return false;
      if (uploadedContentHashes.has(hash)) return true;
      return listingDraftHasMatchingContentHash(
        collectListingDraftContentHashes(images),
        hash,
      );
    },
    [images, uploadedContentHashes],
  );

  const buildImageMetadata = useCallback(
    (
      originalFile: File,
      processedFile: File,
      contentHash: string,
    ): ImageMetadata => ({
      originalName: originalFile.name,
      originalSize: originalFile.size,
      originalType: originalFile.type,
      compressedSize: processedFile.size,
      compressedType: processedFile.type,
      uploadTime: new Date().toISOString(),
      uploadedBy: userId,
      hash: contentHash,
      signature: buildListingImageSignature(contentHash),
      fileKey: contentHash,
      source: previewMode ? "local" : "remote",
    }),
    [previewMode, userId],
  );

  const releaseContentHash = useCallback((hash?: string) => {
    if (!hash) return;
    setUploadedContentHashes((prev) => {
      const next = new Set(prev);
      next.delete(hash);
      return next;
    });
  }, []);

  const markContentHashProcessed = useCallback((hash: string) => {
    setUploadedContentHashes((prev) => new Set(prev).add(hash));
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }

      try {
        setUploading(true);
        setError("");

        if (!userId) {
          setError("You must be logged in to upload images.");
          return;
        }

        // Calculate how many files we can process
        const filesToProcess = acceptedFiles.slice(
          0,
          maxImages - activeImagesCount,
        );
        setProcessingCount(filesToProcess.length);

        // Validate file sizes and types
        const validFiles = filesToProcess.filter((file) => {
          if (file.size > 5 * 1024 * 1024) {
            // 5MB limit
            setError(`${file.name} is too large. Maximum size is 5MB.`);
            return false;
          }
          if (
            !["image/jpeg", "image/png", "image/gif", "image/webp"].includes(
              file.type,
            )
          ) {
            setError(
              `${file.name} has an unsupported format. Please use JPEG, PNG, GIF, or WebP.`,
            );
            return false;
          }
          return true;
        });

        if (validFiles.length === 0) {
          setProcessingCount(0);
          return;
        }

        const coachSettings = await fetchListingPhotoCoachSettings();

        // Process each file sequentially with proper position tracking
        const currentImageCount = activeImagesCount;

        for (const [index, file] of validFiles.entries()) {
          try {
            setUploadStage("checking");
            const validation = await validateListingImageFile(
              file,
              coachSettings,
            );

            captureListingPhotoGateResult({
              validation,
              settings: coachSettings,
            });

            if (validation.blocked) {
              setError(
                validation.message ??
                  `${file.name} did not pass photo quality checks.`,
              );
              continue;
            }

            if (validation.warned && validation.message) {
              showToast(validation.message, "info");
            }

            setUploadStage("compressing");
            const compressedFile = await withTimeout(
              imageUtils.compressListingImage(file),
              LISTING_IMAGE_COMPRESS_TIMEOUT_MS,
              `${file.name}: Compression took too long. Try a smaller image, close other tabs, or refresh and try again.`,
            );

            setUploadStage("finishing");
            const contentHash =
              await computeListingImageContentHashFromBlob(compressedFile);
            const contentSignature = buildListingImageSignature(contentHash);

            if (hasContentHashConflict(contentHash)) {
              showToast(
                `Image "${file.name}" matches an existing upload. Please choose a different image.`,
                "info",
              );
              continue;
            }

            if (!itemId || previewMode) {
              // Local handling (for new items or preview mode)
              const tempUrl = URL.createObjectURL(compressedFile);
              const metadata = buildImageMetadata(
                file,
                compressedFile,
                contentHash,
              );

              const tempImage: ItemImage = {
                id: `temp-${uuidv4()}`, // Prefix with temp- to identify
                itemId: itemId || "",
                url: tempUrl,
                position: currentImageCount + index, // Use incremental position
                isMain: currentImageCount === 0 && index === 0, // Only first image of first batch is main
                file: compressedFile,
                hash: contentHash,
                signature: contentSignature,
                metadata,
              };

              // Update local state immediately
              setImages((prev) => {
                const newImages = [...prev, tempImage];
                return newImages;
              });

              // Call callback immediately
              onImageUploaded?.(tempImage);
              markContentHashProcessed(contentHash);
            } else {
              // Server upload (for existing items when NOT in preview mode)
              const existsRemotely = await imageUtils.checkImageExists(
                contentHash,
                contentSignature,
              );

              if (existsRemotely) {
                showToast(
                  `Image "${file.name}" already exists in the system. Please upload a different photo.`,
                  "info",
                );
                continue;
              }

              const metadata = buildImageMetadata(
                file,
                compressedFile,
                contentHash,
              );
              const uploadedImage = await imageUtils.uploadItemImage({
                file: compressedFile,
                itemId,
                isMain: currentImageCount === 0 && index === 0,
                metadata,
                skipCompression: true,
              });

              if (uploadedImage) {
                setImages((prev) => [...prev, uploadedImage]);
                onImageUploaded?.(uploadedImage);
                markContentHashProcessed(contentHash);
              } else {
                console.error(`Failed to upload file: ${file.name}`);
                setError(
                  `Failed to upload ${file.name}. The image might be a duplicate or there was a server error.`,
                );
              }
            }
          } catch (err) {
            console.error("Error processing image:", err);
            const errorMessage =
              err instanceof Error ? err.message : "Unknown error";
            setError(`Failed to process ${file.name}: ${errorMessage}`);
            break; // Stop processing on first error
          }
        }
      } catch (err) {
        console.error("Error in image upload:", err);
        setError("An error occurred while uploading images. Please try again.");
      } finally {
        setUploading(false);
        setUploadStage(null);
        setProcessingCount(0);
      }
    },
    [
      itemId,
      activeImagesCount,
      maxImages,
      userId,
      onImageUploaded,
      showToast,
      previewMode,
      buildImageMetadata,
      hasContentHashConflict,
      markContentHashProcessed,
    ],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
    },
    maxFiles: Math.max(0, maxImages - activeImagesCount),
    disabled: uploading || activeImagesCount >= maxImages,
    multiple: true,
    noClick: false,
    noKeyboard: false,
    preventDropOnDocument: true,
  });

  const hardRemoveLocalImage = useCallback(
    (imageId: string) => {
      const imageToDelete = images.find((img) => img.id === imageId);
      if (!imageToDelete) return;

      if (imageToDelete.url?.startsWith("blob:")) {
        URL.revokeObjectURL(imageToDelete.url);
      }

      releaseContentHash(
        imageToDelete.hash ||
          (typeof imageToDelete.metadata?.hash === "string"
            ? imageToDelete.metadata.hash
            : undefined),
      );

      setImages((prev) => {
        const nextImages = prev.filter((img) => img.id !== imageId);
        const activeImages = nextImages.filter(
          (img) => !isMarkedForDelete(img),
        );
        const hasActiveMain = activeImages.some((img) => img.isMain);
        if (!hasActiveMain && activeImages.length > 0) {
          const fallbackMainId = activeImages[0].id;
          return nextImages.map((img) => ({
            ...img,
            isMain: img.id === fallbackMainId,
          }));
        }
        return nextImages;
      });

      onImageDeleted?.(imageId, "delete");
    },
    [images, isMarkedForDelete, onImageDeleted, releaseContentHash],
  );

  const handleDeleteImage = async (imageId: string) => {
    try {
      setError("");

      const imageToToggle = images.find((img) => img.id === imageId);
      if (!imageToToggle) {
        return;
      }

      // Preview / draft: delete policy by image state (not page mode alone).
      // Local-only temp images hard-remove; persisted images mark-for-delete.
      if (previewMode) {
        if (shouldHardDeleteListingImage(imageToToggle)) {
          hardRemoveLocalImage(imageId);
          return;
        }

        const willBeMarked = !isMarkedForDelete(imageToToggle);
        setImages((prev) => {
          const nextImages = prev.map((img) =>
            img.id === imageId
              ? {
                  ...img,
                  metadata: {
                    ...(img.metadata || {}),
                    __pendingDelete: willBeMarked,
                  },
                }
              : img,
          );

          // Ensure a non-deleted image remains main after marking current main for delete.
          const activeImages = nextImages.filter(
            (img) => !isMarkedForDelete(img),
          );
          const hasActiveMain = activeImages.some((img) => img.isMain);
          if (!hasActiveMain && activeImages.length > 0) {
            const fallbackMainId = activeImages[0].id;
            return nextImages.map((img) => ({
              ...img,
              isMain: img.id === fallbackMainId,
            }));
          }

          return nextImages;
        });

        onImageDeleted?.(imageId, willBeMarked ? "mark" : "unmark");
        return;
      }

      // If no itemId, remove locally.
      if (!itemId) {
        hardRemoveLocalImage(imageId);
        return;
      }

      if (user?.id && !forcePermission) {
        const hasPermission = await imageUtils.canManageItemImages(
          user.id,
          itemId,
        );
        if (!hasPermission) {
          setError(
            "You do not have permission to delete images for this item.",
          );
          return;
        }
      } else if (!user?.id) {
        setError("You must be logged in to delete images.");
        return;
      }

      const success = await imageUtils.deleteItemImage(imageId);

      if (success) {
        const imageToDelete = images.find((img) => img.id === imageId);
        releaseContentHash(
          imageToDelete?.hash ||
            (typeof imageToDelete?.metadata?.hash === "string"
              ? imageToDelete.metadata.hash
              : undefined),
        );

        setImages((prev) => prev.filter((img) => img.id !== imageId));
        onImageDeleted?.(imageId, "delete");
      } else {
        setError("Failed to delete image. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting image:", err);
      setError("An error occurred while deleting the image.");
    }
  };

  const handleSetMainImage = async (imageId: string) => {
    try {
      setError("");

      // If no itemId or in preview mode, just update local state
      if (!itemId || previewMode) {
        setImages((prev) =>
          prev.map((img) => ({
            ...img,
            isMain: img.id === imageId,
          })),
        );
        if (onMainImageChanged) {
          onMainImageChanged(imageId);
        }
        return;
      }

      if (user?.id && !forcePermission) {
        const hasPermission = await imageUtils.canManageItemImages(
          user.id,
          itemId,
        );
        if (!hasPermission) {
          setError(
            "You do not have permission to set the main image for this item.",
          );
          return;
        }
      } else if (!user?.id) {
        setError("You must be logged in to set the main image.");
        return;
      }

      const success = await imageUtils.setMainImage(imageId, itemId);

      if (success) {
        setImages((prev) =>
          prev.map((img) => ({
            ...img,
            isMain: img.id === imageId,
          })),
        );
        if (onMainImageChanged) {
          onMainImageChanged(imageId);
        }
      } else {
        setError("Failed to set main image. Please try again.");
      }
    } catch (err) {
      console.error("Error setting main image:", err);
      setError("An error occurred while setting the main image.");
    }
  };

  // Update the display count to include processing images
  const totalImageCount = activeImagesCount + processingCount;
  const pendingDeleteCount = images.length - activeImagesCount;
  const availableSlots = Math.max(0, maxImages - activeImagesCount);

  return (
    <ErrorBoundary>
      <div className="w-full space-y-4">
        {activeImagesCount < maxImages && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
 ${isDragActive ? "border-blue-400 bg-blue-50" : "border-border hover:border-border-strong"}
 ${uploading ? "opacity-50 cursor-not-allowed" : ""}
 `}
            role="button"
            tabIndex={0}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center space-y-2">
              <ArrowUpTrayIcon className="h-8 w-8 text-text-light" />
              <p className="text-sm text-text-light">
                {uploading
                  ? getUploadStatusText(
                      uploadStage,
                      isDragActive,
                      availableSlots,
                    )
                  : getUploadStatusText(null, isDragActive, availableSlots)}
              </p>
              <p className="text-xs text-text-light">
                {`${totalImageCount}/${maxImages} images`}
                {processingCount > 0 && ` (${processingCount} uploading...)`}
                {pendingDeleteCount > 0 &&
                  ` (${pendingDeleteCount} marked for delete)`}
              </p>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-50 p-3 rounded-md">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        {/* Image grid */}
        <div className="grid grid-cols-3 sm:grid-cols-3 gap-4">
          {images.map((image, index) => (
            <div key={image.id} className="relative aspect-square">
              <div
                className={
                  isMarkedForDelete(image) ? "opacity-45 grayscale" : ""
                }
              >
                <OptimizedImage
                  src={image.url}
                  alt={image.caption || `Image ${index + 1}`}
                  width={300}
                  height={300}
                  className="rounded-lg"
                  objectFit="cover"
                  onError={() =>
                    setError(
                      `Failed to load image: ${image.caption || `Image ${index + 1}`}`,
                    )
                  }
                />
              </div>
              <div className="absolute top-2 right-2 flex space-x-1">
                <button
                  type="button"
                  onClick={() => handleDeleteImage(image.id)}
                  className={`p-1 text-white rounded-md transition-colors ${
                    isMarkedForDelete(image)
                      ? "bg-tertiary hover:bg-tertiary"
                      : "bg-red-500 hover:bg-red-600"
                  }`}
                  title={
                    isMarkedForDelete(image)
                      ? "Undo delete mark"
                      : "Mark image for delete"
                  }
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleSetMainImage(image.id)}
                  className={`p-1 ${image.isMain ? "bg-primary" : "bg-tertiary hover:bg-primary-600"} text-white rounded-md transition-colors`}
                  title={image.isMain ? "Main image" : "Set as main image"}
                  disabled={image.isMain || isMarkedForDelete(image)}
                >
                  <StarIcon
                    className="h-4 w-4"
                    fill={image.isMain ? "currentColor" : "none"}
                  />
                </button>
              </div>
              {isMarkedForDelete(image) && (
                <div className="absolute inset-x-2 bottom-2 rounded-md bg-red-600/95 px-2 py-1 text-center text-xs font-medium text-white">
                  Marked for delete
                </div>
              )}
              {image.isMain && (
                <div className="absolute bottom-2 left-2 p-1 bg-primary text-white text-xs rounded-md">
                  Main Image
                </div>
              )}
            </div>
          ))}
        </div>

        {images.length > 0 && (
          <div className="text-sm text-text-light">
            <p>
              Click the star icon to set an image as the main image that will
              appear first in search results.
            </p>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

/** Stable props signature — JSON.stringify drops `File`, so we compare ids + pending state explicitly. */
function draftImagesSignature(images?: ItemImage[]): string {
  if (!images?.length) return "";
  return JSON.stringify(
    images.map((img) => ({
      id: img.id,
      url: img.url,
      position: img.position,
      isMain: img.isMain,
      pendingDelete:
        Boolean(img.metadata) &&
        typeof img.metadata === "object" &&
        img.metadata !== null &&
        "__pendingDelete" in img.metadata &&
        (img.metadata as { __pendingDelete?: boolean }).__pendingDelete ===
          true,
      hasFile: Boolean(img.file),
    })),
  );
}

export default withMemo(ImageUploader, (prev, next) => {
  return (
    prev.itemId === next.itemId &&
    prev.maxImages === next.maxImages &&
    prev.forcePermission === next.forcePermission &&
    prev.previewMode === next.previewMode &&
    draftImagesSignature(prev.initialImages) ===
      draftImagesSignature(next.initialImages)
  );
});
